import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Clock, Languages, ArrowRight, RotateCcw } from "lucide-react";
import "./i18n";
import { LangSwitcher } from "./components/LangSwitcher";
import { Wizard } from "./components/Wizard";
import { ResultView } from "./components/results";
import type { QualificationResult, WizardAnswers, Lang } from "./lib/types";

type View =
  | { kind: "home" }
  | { kind: "wizard" }
  | { kind: "result"; result: QualificationResult; answers: WizardAnswers; submissionId: string | null };

export default function App() {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<View>({ kind: "home" });
  const lang = i18n.language as Lang;

  return (
    <div className="flex min-h-[100dvh] flex-col bg-cream">
      <header className="sticky top-0 z-20 border-b border-border bg-paper/95 backdrop-blur">
        <div className="mx-auto flex max-w-marketing items-center justify-between px-4 py-3 md:px-8">
          <button
            type="button"
            onClick={() => setView({ kind: "home" })}
            className="flex items-center gap-3"
            aria-label="LevelCare — Accueil"
          >
            <img src="/logo-levelcare.svg" alt="LevelCare" className="h-7 w-auto md:h-8" />
            <span className="hidden text-caption text-muted md:inline">{t("header.tagline")}</span>
          </button>
          <LangSwitcher />
        </div>
      </header>

      <main className="flex-1">
        {view.kind === "home" && <Home onStart={() => setView({ kind: "wizard" })} />}

        {view.kind === "wizard" && (
          <Wizard
            source="direct"
            onComplete={(result, answers, submissionId) =>
              setView({ kind: "result", result, answers, submissionId })
            }
          />
        )}

        {view.kind === "result" && (
          <>
            <ResultView result={view.result} answers={view.answers} lang={lang} />
            <div className="mx-auto mt-6 max-w-wizard px-4 pb-12 md:px-0">
              <button
                type="button"
                onClick={() => setView({ kind: "home" })}
                className="inline-flex items-center gap-2 text-caption text-muted hover:text-ink"
              >
                <RotateCcw size={16} strokeWidth={1.75} />
                Recommencer
              </button>
            </div>
          </>
        )}
      </main>

      <footer className="border-t border-border bg-paper safe-bottom">
        <div className="mx-auto max-w-marketing px-4 py-6 md:px-8">
          <p className="text-caption text-muted">{t("footer.address")}</p>
          <p className="mt-1 text-caption text-muted">{t("footer.contact")}</p>
        </div>
      </footer>
    </div>
  );
}

function Home({ onStart }: { onStart: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-wizard px-4 py-12 md:px-0 md:py-20">
      <h1 className="text-balance">{t("home.title")}</h1>
      <p className="mt-6 text-body-long text-ink-soft">{t("home.subtitle")}</p>

      <button
        type="button"
        onClick={onStart}
        className="btn-primary mt-10 w-full justify-center md:w-auto"
      >
        {t("home.cta")}
        <ArrowRight size={20} strokeWidth={1.75} />
      </button>

      <ul className="mt-12 grid gap-6 md:grid-cols-3">
        <Trust icon={<ShieldCheck size={20} strokeWidth={1.75} />} label={t("home.trust.secure")} />
        <Trust icon={<Clock size={20} strokeWidth={1.75} />} label={t("home.trust.fast")} />
        <Trust icon={<Languages size={20} strokeWidth={1.75} />} label={t("home.trust.multi")} />
      </ul>
    </div>
  );
}

function Trust({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <li className="flex items-center gap-3 text-caption text-muted">
      <span className="text-ink-soft">{icon}</span>
      <span>{label}</span>
    </li>
  );
}
